import { DndContext, closestCenter } from '@dnd-kit/core'
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable'
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Container,
  Group,
  Loader,
  Menu,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Stepper,
  Text,
  TextInput,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core'
import { Dropzone } from '@mantine/dropzone'
import { notifications } from '@mantine/notifications'
import { IconBookmark, IconCopy, IconFileExport, IconLogout, IconTrash, IconUpload } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { shouldSkipAuth, useAuth } from './auth'
import { getEnvironment } from './auth/config'
import { JoyAnimatedImage } from './components/JoyAnimatedImage'
import { SortableItem } from './components/SortableItem'
import { TiptapEditor } from './components/TiptapEditor'
import { initGainsight, useGainsight } from './context/analytics/gainsight'
import reportIcon from '/dd-favicon.svg'

// HappyCo notification styles that match the design system
const notificationStyles = `
  /* Add global background color */
  body {
    background-color: #f8f9fa !important;
    min-height: 100vh !important;
    margin: 0 !important;
  }

  [data-notifications-container] {
    position: fixed !important;
    left: 20px !important;
    right: auto !important;
    bottom: 20px !important;
    top: auto !important;
    z-index: 9999 !important;
  }

  .mantine-Notification-root {
    background-color: #232A5C !important;
    border: none !important;
    border-left: none !important;
    border-radius: 8px !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
    padding: 16px !important;
    position: relative !important;
    max-width: 400px !important;
  }
  
  .mantine-Notification-root::before {
    display: none !important;
  }
  
  .mantine-Notification-title {
    color: white !important;
    font-weight: 600 !important;
    margin-bottom: 4px !important;
    font-family: 'proxima-nova', 'Proxima Nova', sans-serif !important;
  }
  
  .mantine-Notification-description {
    color: white !important;
    opacity: 0.9 !important;
    font-family: 'proxima-nova', 'Proxima Nova', sans-serif !important;
  }
  
  .mantine-Notification-closeButton {
    color: #9ca3af !important;
    border-radius: 6px !important;
  }
  
  .mantine-Notification-closeButton:hover {
    background-color: rgba(255, 255, 255, 0.1) !important;
  }

  .mantine-Notification-icon {
    color: white !important;
  }

  /* Override any other notification positioning */
  .mantine-Notifications-root {
    position: fixed !important;
    left: 20px !important;
    bottom: 20px !important;
    right: auto !important;
    top: auto !important;
  }

  /* Dropdown styling improvements - remove borders and add hover effects */
  .mantine-Select-dropdown {
    border: none !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
    border-radius: 8px !important;
  }

  .mantine-Select-option {
    padding: 8px 12px !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    min-height: 36px !important;
    display: flex !important;
    align-items: center !important;
    font-size: 14px !important;
  }

  .mantine-Select-option:hover {
    background-color: rgba(35, 42, 92, 0.1) !important;
  }

  /* Remove all borders from Select inputs */
  .mantine-Select-input {
    border: 1px solid #e9ecef !important;
    border-radius: 8px !important;
    outline: none !important;
  }

  .mantine-Select-input:focus {
    border: 1px solid #635BFF !important;
    border-radius: 8px !important;
    outline: none !important;
    box-shadow: 0 0 0 2px rgba(99, 91, 255, 0.1) !important;
  }

  .mantine-Select-input:focus-within {
    border: 1px solid #635BFF !important;
    border-radius: 8px !important;
    outline: none !important;
    box-shadow: 0 0 0 2px rgba(99, 91, 255, 0.1) !important;
  }

  /* Add light grey hover effect for disabled/placeholder state dropdowns */
  .mantine-Select-input:hover {
    background-color: rgba(35, 42, 92, 0.05) !important;
  }

  .mantine-Select-input[data-placeholder="true"]:hover {
    background-color: rgba(35, 42, 92, 0.1) !important;
  }

  /* Popover outline item hover effects */
  .outline-item {
    border-radius: 8px !important;
    cursor: pointer !important;
    transition: background-color 0.15s ease !important;
    border: 1px solid #e9ecef !important;
  }

  .outline-item:hover {
    background-color: rgba(35, 42, 92, 0.05) !important;
  }

  .outline-item.selected {
    background-color: rgba(35, 42, 92, 0.1) !important;
  }

  .outline-item.selected:hover {
    background-color: rgba(35, 42, 92, 0.15) !important;
  }

  /* Tiptap Editor Styles */
  .tiptap {
    outline: none;
    border-radius: 8px;
    min-height: 280px;
  }

  .tiptap p.is-editor-empty:first-of-type::before {
    content: attr(data-placeholder);
    float: left;
    color: #adb5bd;
    pointer-events: none;
    height: 0;
    font-style: italic;
  }

  .tiptap h1, .tiptap h2, .tiptap h3, .tiptap h4, .tiptap h5, .tiptap h6 {
    color: #232A5C;
    margin-top: 16px;
    margin-bottom: 8px;
    font-weight: 600;
  }

  .tiptap h1 { font-size: 24px; }
  .tiptap h2 { font-size: 20px; }
  .tiptap h3 { font-size: 18px; }
  .tiptap h4 { font-size: 16px; }
  .tiptap h5 { font-size: 14px; }
  .tiptap h6 { font-size: 12px; }

  .tiptap p {
    margin-bottom: 12px;
    line-height: 1.6;
  }

  .tiptap ul, .tiptap ol {
    margin-bottom: 12px;
    padding-left: 20px;
  }

  .tiptap li {
    margin-bottom: 4px;
  }

  .tiptap blockquote {
    border-left: 4px solid #635BFF;
    padding-left: 16px;
    margin: 16px 0;
    color: #64748b;
    font-style: italic;
    background-color: #f8f9ff;
    padding: 12px 16px;
    border-radius: 0 8px 8px 0;
  }

  .tiptap code {
    background-color: #f1f5f9;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 13px;
    color: #1e293b;
  }

  .tiptap pre {
    background-color: #1e293b;
    color: #e2e8f0;
    padding: 16px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 16px 0;
  }

  .tiptap pre code {
    background-color: transparent;
    padding: 0;
    color: inherit;
  }

  .tiptap strong {
    font-weight: 600;
    color: #232A5C;
  }

  .tiptap em {
    font-style: italic;
  }

  .tiptap mark {
    background-color: #fef3c7;
    color: #92400e;
    padding: 2px 4px;
    border-radius: 3px;
  }

  .tiptap hr {
    border: none;
    border-top: 2px solid #e2e8f0;
    margin: 24px 0;
  }

  .tiptap a {
    color: #635BFF;
    text-decoration: underline;
  }

  .tiptap a:hover {
    color: #4f46e5;
  }

  /* Focus styles */
  .tiptap:focus {
    outline: none;
  }

  /* Disabled state */
  .tiptap[contenteditable="false"] {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

export default function App() {
  // Only use auth context if not skipping auth
  const authData = shouldSkipAuth() ? null : useAuth()
  const user = authData?.user || {
    id: 'dev-user',
    email: 'dev@localhost',
    firstName: 'Dev',
    name: 'Dev User',
    sub: 'dev-user',
  }
  const logout = authData?.logout || (() => console.log('Auth skipped - logout not available'))

  // Initialize Gainsight analytics
  useEffect(() => {
    const environment = getEnvironment()
    initGainsight(environment)
  }, [])

  // Use Gainsight hook for user tracking
  useGainsight(user)

  // Inject HappyCo notification styles
  useEffect(() => {
    const style = document.createElement('style')
    style.textContent = notificationStyles
    document.head.appendChild(style)

    // Set background color on mount
    document.body.style.backgroundColor = '#f8f9fa'
    document.body.style.minHeight = '100vh'
    document.body.style.margin = '0'

    return () => {
      document.head.removeChild(style)
    }
  }, [])

  // Create audio context for better sound effects
  const createAudioContext = () => {
    if (typeof window !== 'undefined' && window.AudioContext) {
      return new (window.AudioContext || (window as any).webkitAudioContext)()
    }
    return null
  }

  // Generate start sound (notification chime)
  const playStartSound = () => {
    const audioContext = createAudioContext()
    if (!audioContext) return

    const oscillator = audioContext.createOscillator()
    const gainNode = audioContext.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(audioContext.destination)

    oscillator.frequency.setValueAtTime(800, audioContext.currentTime)
    oscillator.frequency.exponentialRampToValueAtTime(1200, audioContext.currentTime + 0.1)

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3)

    oscillator.start(audioContext.currentTime)
    oscillator.stop(audioContext.currentTime + 0.3)
  }

  // Generate completion sound (success chime)
  const playCompleteSound = () => {
    const audioContext = createAudioContext()
    if (!audioContext) return

    const oscillator = audioContext.createOscillator()
    const gainNode = audioContext.createGain()

    // Play a pleasant chord
    oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime) // C5
    oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1) // E5
    oscillator.frequency.setValueAtTime(783.99, audioContext.currentTime + 0.2) // G5

    gainNode.gain.setValueAtTime(0.2, audioContext.currentTime)
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5)

    oscillator.start(audioContext.currentTime)
    oscillator.stop(audioContext.currentTime + 0.5)
  }

  // Existing state variables
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [promptText, setPromptText] = useState('')
  const [summaryText, setSummaryText] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [_, setActiveSteps] = useState<string>('upload')
  const [activeStep, setActiveStep] = useState(0)
  const [selectedOutlineName, setSelectedOutlineName] = useState<string | null>(null)
  const [selectedPromptName, setSelectedPromptName] = useState<string | null>(null)
  const [outlinesModalOpen, setOutlinesModalOpen] = useState(false)
  const [promptPopoverOpen, setPromptPopoverOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteType, setDeleteType] = useState<'template' | 'prompt'>('template')
  const [itemToDelete, setItemToDelete] = useState('')
  const [newOutlineName, setNewOutlineName] = useState('')
  const [newPromptName, setNewPromptName] = useState('')
  const [outlineSearchText, setOutlineSearchText] = useState('')
  const [promptSearchText, setPromptSearchText] = useState('')

  const [savedPrompts, setSavedPrompts] = useState(() => {
    if (typeof window !== 'undefined') {
      return JSON.parse(localStorage.getItem('savedPrompts') || '[]')
    }
    return []
  })

  const [savedTemplates, setSavedTemplates] = useState(() => {
    if (typeof window !== 'undefined') {
      return JSON.parse(localStorage.getItem('savedTemplates') || '[]')
    }
    return []
  })
  const [sections, setSections] = useState([
    'Executive Summary',
    'Lease File Review Summary',
    'Unit Inspections Summary',
    'Work Order Summary',
    'Service Contracts',
    'Facilities Inspection Summary',
    'Market Analysis',
    'Key Findings and Recommendations',
  ])
  const [newSection, setNewSection] = useState('')

  // Completion logic
  const isUploadComplete = uploadedFiles.length > 0
  const isInstructionsComplete = promptText.trim().length > 0
  const isOutlineComplete = sections.length > 0

  // Check if all steps are ready for generation
  const allStepsReady = isUploadComplete && isInstructionsComplete && isOutlineComplete

  // Tooltip for disabled generate button
  const incompleteSteps = []
  if (!isUploadComplete) incompleteSteps.push('Upload Documents')
  if (!isInstructionsComplete) incompleteSteps.push('Custom Instructions')
  if (!isOutlineComplete) incompleteSteps.push('Report Outline')

  const disabledTooltipLabel =
    incompleteSteps.length > 0 ? `Please complete: ${incompleteSteps.join(', ')}` : 'All steps are ready!'

  const handleDragEnd = (event: any) => {
    const { active, over } = event
    if (active.id !== over?.id) {
      const oldIndex = sections.indexOf(active.id)
      const newIndex = sections.indexOf(over.id)
      setSections((items) => arrayMove(items, oldIndex, newIndex))
    }
  }

  const handleAddSection = () => {
    if (newSection.trim()) {
      setSections([...sections, newSection.trim()])
      setNewSection('')
    }
  }

  const handleRemoveSection = (id: string) => {
    setSections((prev) => prev.filter((s) => s !== id))
  }

  const handleRemoveFile = (fileToRemove: File) => {
    setUploadedFiles((prev) => prev.filter((file) => file !== fileToRemove))
  }

  // Function to open delete confirmation modal
  const openDeleteModal = (type: 'template' | 'prompt', itemName: string) => {
    setDeleteType(type)
    setItemToDelete(itemName)
    setDeleteModalOpen(true)
  }

  // Function to handle confirmed deletion
  const handleConfirmDelete = () => {
    if (deleteType === 'template') {
      const template = savedTemplates.find((t: any) => t.name === itemToDelete)
      if (template) {
        const index = savedTemplates.indexOf(template)
        handleRemoveTemplate(index)
      }
    } else {
      const prompt = savedPrompts.find((p: any) => p.name === itemToDelete)
      if (prompt) {
        const index = savedPrompts.indexOf(prompt)
        handleRemovePrompt(index)
      }
    }
    setDeleteModalOpen(false)
    setItemToDelete('')
  }

  const handleRemoveTemplate = (templateIndex: number) => {
    const templateName = savedTemplates[templateIndex].name
    const updated = savedTemplates.filter((_: any, index: number) => index !== templateIndex)
    localStorage.setItem('savedTemplates', JSON.stringify(updated))
    setSavedTemplates(updated)

    // Clear selected outline name if the removed template was selected
    if (selectedOutlineName === templateName) {
      setSelectedOutlineName(null)
    }

    notifications.show({
      title: 'Delete Successful',
      message: `Outline "${templateName}" has been removed.`,
      position: 'bottom-left',
    })
  }

  const handleRemovePrompt = (promptIndex: number) => {
    const promptName = savedPrompts[promptIndex].name
    const updated = savedPrompts.filter((_: any, index: number) => index !== promptIndex)
    localStorage.setItem('savedPrompts', JSON.stringify(updated))
    setSavedPrompts(updated)

    // Clear selected prompt name if the removed prompt was selected
    if (selectedPromptName === promptName) {
      setSelectedPromptName(null)
    }

    notifications.show({
      title: 'Delete Successful',
      message: `Prompt "${promptName}" has been removed.`,
      position: 'bottom-left',
    })
  }

  // Filter saved outlines based on search text
  const filteredOutlines = savedTemplates.filter((template: any) =>
    template.name.toLowerCase().includes(outlineSearchText.toLowerCase())
  )

  // Handle selecting an existing outline
  const handleSelectOutline = (template: any) => {
    setSections(template.structure)
    setSelectedOutlineName(template.name)
    setOutlineSearchText('')
    setOutlinesModalOpen(false)
  }

  // Handle saving a new outline
  const handleSaveNewOutline = () => {
    if (newOutlineName.trim()) {
      // Check for duplicate names
      const nameExists = savedTemplates.some((t: any) => t.name.toLowerCase() === newOutlineName.toLowerCase())
      if (nameExists) {
        notifications.show({
          title: 'Error',
          message: 'An outline with this name already exists. Please choose a different name.',
          position: 'bottom-left',
        })
        return
      }

      const updated = [...savedTemplates, { name: newOutlineName, structure: sections }]
      localStorage.setItem('savedTemplates', JSON.stringify(updated))
      setSavedTemplates(updated)
      setSelectedOutlineName(newOutlineName)
      setNewOutlineName('')
      notifications.show({
        title: 'Success',
        message: 'Outline saved!',
        position: 'bottom-left',
      })
    } else {
      notifications.show({
        title: 'Error',
        message: 'Please enter an outline name.',
        position: 'bottom-left',
      })
    }
  }

  // Filter saved prompts based on search text
  const filteredPrompts = savedPrompts.filter((prompt: any) =>
    prompt.name.toLowerCase().includes(promptSearchText.toLowerCase())
  )

  // Handle selecting an existing prompt
  const handleSelectPrompt = (prompt: any) => {
    setPromptText(prompt.content)
    setSelectedPromptName(prompt.name)
    setPromptPopoverOpen(false)
    setPromptSearchText('')
  }

  // Handle saving a new prompt
  const handleSaveNewPrompt = () => {
    if (newPromptName.trim() && promptText.trim()) {
      // Check for duplicate names
      const nameExists = savedPrompts.some((p: any) => p.name.toLowerCase() === newPromptName.toLowerCase())
      if (nameExists) {
        notifications.show({
          title: 'Error',
          message: 'A prompt with this name already exists. Please choose a different name.',
          position: 'bottom-left',
        })
        return
      }

      const updated = [...savedPrompts, { name: newPromptName, content: promptText }]
      localStorage.setItem('savedPrompts', JSON.stringify(updated))
      setSavedPrompts(updated)
      setSelectedPromptName(newPromptName)
      setNewPromptName('')
      setPromptPopoverOpen(false)
      notifications.show({
        title: 'Success',
        message: 'Prompt saved!',
        position: 'bottom-left',
      })
    } else {
      notifications.show({
        title: 'Error',
        message: 'Please enter both a prompt name and content.',
        position: 'bottom-left',
      })
    }
  }

  const handleLogout = () => {
    logout()
    notifications.show({
      title: 'Success',
      message: 'You have been successfully logged out.',
      position: 'bottom-left',
    })
  }

  // Function to call the backend API
  const handleGenerateSummary = async () => {
    if (!allStepsReady || isGenerating) return

    // Play start sound
    playStartSound()

    setIsGenerating(true)
    setSummaryText('')

    try {
      const shouldUseMock = import.meta.env.VITE_MOCK_BACKEND === 'true'

      if (shouldUseMock) {
        // Mock mode: simulate API call with delay
        await new Promise((resolve) => setTimeout(resolve, 2000))

        const mockSummary = `# Report Summary

## Executive Overview
This comprehensive analysis of the property portfolio reveals several key findings that warrant attention from potential investors and stakeholders.

## Financial Performance
- **Revenue Growth**: 12% year-over-year increase in rental income
- **Occupancy Rate**: Currently at 94%, above market average of 89%
- **Operating Expenses**: 23% of gross revenue, within acceptable range

## Risk Assessment
### High Priority Issues
1. **Deferred Maintenance**: $450,000 in identified capital improvements needed
2. **Lease Expirations**: 15% of units expiring within 12 months
3. **Market Competition**: New developments in area may impact rental rates

### Medium Priority Issues
1. **Utility Infrastructure**: Aging HVAC systems require replacement within 3-5 years
2. **Regulatory Compliance**: Minor violations in fire safety systems
3. **Tenant Mix**: Concentration risk with 25% of revenue from single tenant

## Recommendations
1. **Immediate Actions**: Address critical safety violations within 30 days
2. **Short-term (3-6 months)**: Implement lease renewal strategy for expiring contracts
3. **Long-term (1-2 years)**: Develop capital improvement plan for major systems

## Market Analysis
The property is located in a growing submarket with strong fundamentals:
- Population growth: 8% annually
- Employment growth: 5% year-over-year
- New supply pipeline: Limited competition expected

## Conclusion
While the property shows strong operational performance, significant capital investment will be required to maintain competitive position and address deferred maintenance issues.`

        setSummaryText(mockSummary)
      } else {
        const formData = new FormData()

        // Add files
        uploadedFiles.forEach((file) => {
          formData.append('files', file)
        })

        // Add required fields
        formData.append('business_id', '12345') // Hardcoded for now
        formData.append('report_outline', JSON.stringify(sections))

        // Add custom instructions if provided
        if (promptText.trim()) {
          formData.append('custom_instructions', promptText)
        }

        // Get backend URL from window config or fallback to localhost for development
        const backendUrl = (window as any).REPORT_SUMMARIZER_CONFIG?.BACKEND_URL || 'http://localhost:8080'
        const response = await fetch(`${backendUrl}/api/summarize`, {
          method: 'POST',
          body: formData,
        })

        const data = await response.json()

        if (!response.ok) {
          throw new Error(data.error || `Request failed: ${response.status}`)
        }

        if (data.success) {
          // Trim any preamble/text before the first markdown heading "#" symbol
          const trimmedSummary = data.summary.includes('#')
            ? data.summary.substring(data.summary.indexOf('#'))
            : data.summary
          setSummaryText(trimmedSummary)
          notifications.show({
            title: 'Joy',
            message: 'Joy has finished writing!',
            position: 'bottom-left',
          })
        } else {
          throw new Error(data.error || 'Generation failed')
        }
      }

      // Play completion sound after a short delay
      setTimeout(() => {
        playCompleteSound()
      }, 500)
    } catch (error) {
      console.error('Error generating summary:', error)
      notifications.show({
        title: 'Generation Failed',
        message: error instanceof Error ? error.message : 'Failed to generate summary. Please try again.',
        color: 'red',
        position: 'bottom-left',
      })
    } finally {
      setIsGenerating(false)
    }
  }

  // Get user's initial from authenticated user data
  const getUserInitial = () => {
    return user?.firstName?.charAt(0) || user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'
  }

  // Helper: when a file is uploaded, go to next step
  const handleFilesDrop = (files: File[]) => {
    setUploadedFiles((prev) => [...prev, ...files])
    // Move to instructions step when files are uploaded
    setActiveSteps('instructions')
  }

  // Helper: when outline is edited, close the step (optional: could add a 'complete' state)
  const handleAddSectionAndClose = () => {
    handleAddSection()
  }

  // Export functions
  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(summaryText)
      notifications.show({
        title: 'Copied!',
        message: 'Summary copied to clipboard',
        color: 'green',
        position: 'bottom-left',
      })
    } catch (err) {
      notifications.show({
        title: 'Copy Failed',
        message: 'Failed to copy to clipboard',
        color: 'red',
        position: 'bottom-left',
      })
    }
  }

  const handleExportPDF = async () => {
    try {
      // Create a new window with the formatted content
      const printWindow = window.open('', '_blank')
      if (!printWindow) {
        throw new Error('Popup blocked')
      }

      // Convert markdown to HTML for better PDF formatting
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Summary</title>
          <style>
            body {
              font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              line-height: 1.6;
              color: #1e293b;
              max-width: 800px;
              margin: 0 auto;
              padding: 40px 20px;
            }
            h1, h2, h3, h4, h5, h6 {
              color: #232A5C;
              margin-top: 24px;
              margin-bottom: 12px;
            }
            h1 { font-size: 28px; }
            h2 { font-size: 24px; }
            h3 { font-size: 20px; }
            p { margin-bottom: 16px; }
            ul, ol { margin-bottom: 16px; padding-left: 24px; }
            blockquote {
              border-left: 4px solid #3b82f6;
              padding-left: 16px;
              margin: 20px 0;
              color: #64748b;
              font-style: italic;
            }
            code {
              background-color: #f1f5f9;
              padding: 2px 6px;
              border-radius: 4px;
              font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
            }
            pre {
              background-color: #1e293b;
              color: #e2e8f0;
              padding: 16px;
              border-radius: 8px;
              overflow-x: auto;
              margin: 20px 0;
            }
            pre code {
              background-color: transparent;
              padding: 0;
              color: inherit;
            }
            .header {
              text-align: center;
              margin-bottom: 40px;
              padding-bottom: 20px;
              border-bottom: 2px solid #e2e8f0;
            }
            .header h1 {
              color: #232A5C;
              margin: 0;
            }
            .header p {
              color: #64748b;
              margin: 8px 0 0 0;
            }
            @media print {
              body { margin: 0; padding: 20px; }
              .header { margin-bottom: 30px; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Summary</h1>
            <p>Generated by Joy on ${new Date().toLocaleDateString()}</p>
          </div>
          <div class="content">
            ${summaryText.replace(/\n/g, '<br>')}
          </div>
        </body>
        </html>
      `

      printWindow.document.write(htmlContent)
      printWindow.document.close()

      // Wait for content to load, then print
      printWindow.onload = () => {
        printWindow.print()
        printWindow.close()
      }

      notifications.show({
        title: 'PDF Export',
        message: 'Opening print dialog for PDF export',
        color: 'blue',
        position: 'bottom-left',
      })
    } catch (err) {
      notifications.show({
        title: 'Export Failed',
        message: 'Failed to export PDF. Please try again.',
        color: 'red',
        position: 'bottom-left',
      })
    }
  }

  const handleExportWord = async () => {
    try {
      // Create a simple HTML document that Word can open
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Summary</title>
          <meta charset="utf-8">
          <style>
            body {
              font-family: 'Calibri', 'Arial', sans-serif;
              line-height: 1.6;
              color: #000000;
              max-width: 800px;
              margin: 0 auto;
              padding: 40px 20px;
            }
            h1, h2, h3, h4, h5, h6 {
              color: #232A5C;
              margin-top: 24px;
              margin-bottom: 12px;
            }
            h1 { font-size: 28px; }
            h2 { font-size: 24px; }
            h3 { font-size: 20px; }
            p { margin-bottom: 16px; }
            ul, ol { margin-bottom: 16px; padding-left: 24px; }
            blockquote {
              border-left: 4px solid #3b82f6;
              padding-left: 16px;
              margin: 20px 0;
              color: #64748b;
              font-style: italic;
            }
            code {
              background-color: #f1f5f9;
              padding: 2px 6px;
              border-radius: 4px;
              font-family: 'Courier New', monospace;
            }
            pre {
              background-color: #f8f9fa;
              border: 1px solid #e9ecef;
              padding: 16px;
              border-radius: 4px;
              overflow-x: auto;
              margin: 20px 0;
            }
            pre code {
              background-color: transparent;
              padding: 0;
              color: inherit;
            }
            .header {
              text-align: center;
              margin-bottom: 40px;
              padding-bottom: 20px;
              border-bottom: 2px solid #e2e8f0;
            }
            .header h1 {
              color: #232A5C;
              margin: 0;
            }
            .header p {
              color: #64748b;
              margin: 8px 0 0 0;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Summary</h1>
            <p>Generated by Joy on ${new Date().toLocaleDateString()}</p>
          </div>
          <div class="content">
            ${summaryText.replace(/\n/g, '<br>')}
          </div>
        </body>
        </html>
      `

      // Create a blob and download link
      const blob = new Blob([htmlContent], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `Due_Diligence_Summary_${new Date().toISOString().split('T')[0]}.docx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      notifications.show({
        title: 'Word Export',
        message: 'Word document downloaded successfully',
        color: 'green',
        position: 'bottom-left',
      })
    } catch (err) {
      notifications.show({
        title: 'Export Failed',
        message: 'Failed to export Word document. Please try again.',
        color: 'red',
        position: 'bottom-left',
      })
    }
  }

  // Button style for consistency
  const navButtonStyle = {
    backgroundColor: '#f8f9fa',
    color: '#232A5C',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontWeight: 600,
  }

  return (
    <Container
      size="xl"
      py="xl"
      style={{
        backgroundColor: '#f8f9fa',
        minHeight: '100vh',
      }}
    >
      <Box
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #e9ecef',
          padding: '32px',
          marginBottom: '32px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        }}
      >
        <div style={{ textAlign: 'left' }}>
          <Group justify="space-between" align="center" mb="16px">
            <Group justify="flex-start" align="center">
              <img
                src={reportIcon}
                alt="Due Diligence Icon"
                style={{ width: '28px', height: '28px', marginRight: '4px' }}
              />
              <Title
                order={1}
                size="h1"
                style={{
                  color: '#232A5C',
                  margin: 0,
                  fontSize: '32px',
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  lineHeight: '1.2',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                Summaries by &nbsp;
                <span style={{ color: '#635BFF' }}>Joy</span>
                <JoyAnimatedImage
                  style={{
                    height: '3.5rem',
                    width: '3.5rem',
                    marginLeft: '8px',
                    display: 'inline-block',
                    verticalAlign: 'middle',
                  }}
                />
              </Title>
            </Group>
            <Menu shadow="md" width={200} position="bottom-end">
              <Menu.Target>
                <Avatar
                  style={{
                    cursor: 'pointer',
                    backgroundColor: '#232A5C',
                    color: 'white',
                    fontWeight: 600,
                  }}
                  size="md"
                  radius="xl"
                >
                  {getUserInitial()}
                </Avatar>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item leftSection={<IconLogout size={16} />} onClick={handleLogout} style={{ color: '#fa5252' }}>
                  Logout
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
          <Text
            size="lg"
            c="dimmed"
            style={{
              fontFamily: 'proxima-nova, sans-serif',
              fontSize: '18px',
              lineHeight: '1.5',
              color: '#6c757d',
              margin: 0,
              maxWidth: '600px',
              whiteSpace: 'nowrap',
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
            }}
          >
            No more piecing together reports by hand, Joy prepares a customizable summary in minutes.
          </Text>
        </div>
      </Box>

      {/* Stepper Progress and Step Content */}
      <Box mb="24px">
        <Stepper
          active={activeStep}
          onStepClick={setActiveStep}
          allowNextStepsSelect={false}
          size="lg"
          styles={{
            root: {
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '1px solid #e9ecef',
              padding: '24px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
            },
            step: {
              cursor: 'pointer',
            },
            stepIcon: {
              borderColor: '#FFB020',
              color: 'white',
              backgroundColor: '#FFB020',
              borderWidth: 2,
              borderRadius: '50%',
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 15,
            },
            separator: {
              backgroundColor: '#e9ecef',
            },
            stepLabel: {
              fontSize: '16px',
              fontWeight: 600,
              color: '#232A5C',
            },
            stepDescription: {
              fontSize: '14px',
              color: '#6c757d',
            },
          }}
        >
          {/* Step 1: Upload Documents */}
          <Stepper.Step label="Upload Documents" description="Upload your files for review">
            {activeStep === 0 && (
              <Paper withBorder shadow="sm" p="24px" style={{ borderRadius: '12px', border: '1px solid #e9ecef' }}>
                <Text
                  size="sm"
                  style={{
                    fontSize: '15px',
                    lineHeight: '1.5',
                    color: '#232A5C',
                    margin: 0,
                    fontWeight: 600,
                    marginBottom: 16,
                  }}
                >
                  Upload your documents for Joy to review.
                </Text>
                <Dropzone
                  data-test-id="DD-Summarizer-Upload-Files"
                  onDrop={handleFilesDrop}
                  onReject={(fileRejections) => {
                    fileRejections.forEach((rejection) => {
                      rejection.errors.forEach((error) => {
                        notifications.show({
                          title: 'File Upload Error',
                          message: `${rejection.file.name}: ${error.message}`,
                          color: 'red',
                          position: 'bottom-left',
                        })
                      })
                    })
                  }}
                  maxSize={10 * 1024 ** 2}
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '2px dashed #e9ecef',
                    borderRadius: '12px',
                  }}
                >
                  <Stack justify="center" align="center" gap="md" mih={120} style={{ pointerEvents: 'none' }}>
                    <Dropzone.Accept>
                      <IconUpload size={36} stroke={1.5} color="#228be6" />
                    </Dropzone.Accept>
                    <Dropzone.Idle>
                      <IconUpload size={36} stroke={1.5} color="#868e96" />
                    </Dropzone.Idle>
                    <Text size="sm" c="dimmed">
                      Browse or drag and drop files here
                      <br />
                      <span style={{ fontSize: '12px', color: '#adb5bd' }}>(max file size 10mb)</span>
                    </Text>
                  </Stack>
                </Dropzone>
                {uploadedFiles.length > 0 && (
                  <Stack gap="xs" mt="md">
                    <Text size="sm" fw={700}>
                      Uploaded Files:
                    </Text>
                    {uploadedFiles.map((file, index) => (
                      <Group
                        key={`${file.name}-${index}`}
                        justify="space-between"
                        p={4}
                        bg="gray.0"
                        style={{ borderRadius: 6 }}
                      >
                        <Group gap="xs" style={{ flex: 1, minWidth: 0 }}>
                          <Text
                            size="sm"
                            style={{
                              flex: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              minWidth: 0,
                            }}
                            title={file.name}
                          >
                            {file.name}
                          </Text>
                          <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                            ({(file.size / 1024).toFixed(1)} KB)
                          </Text>
                        </Group>
                        <ActionIcon
                          data-test-id="DD-Summarizer-Remove-File"
                          variant="transparent"
                          c="gray.6"
                          size="sm"
                          radius="xl"
                          className="close-icon-hover"
                          onClick={() => handleRemoveFile(file)}
                          style={{ flexShrink: 0 }}
                        >
                          <IconTrash size={14} color="red" />
                        </ActionIcon>
                      </Group>
                    ))}
                    <Button
                      data-test-id="DD-Summarizer-Remove-All-Files"
                      variant="light"
                      color="red"
                      size="xs"
                      style={{ alignSelf: 'flex-start', marginTop: 4, borderRadius: '8px' }}
                      onClick={() => setUploadedFiles([])}
                    >
                      Remove All
                    </Button>
                  </Stack>
                )}
                <Group justify="space-between" mt="lg">
                  <Button
                    data-test-id="DD-Summarizer-Back-Button"
                    variant="default"
                    disabled={activeStep === 0}
                    onClick={() => setActiveStep((s) => Math.max(s - 1, 0))}
                    style={navButtonStyle}
                  >
                    Back
                  </Button>
                  <Button
                    data-test-id="DD-Summarizer-Next-Button"
                    onClick={() => setActiveStep((s) => s + 1)}
                    disabled={!isUploadComplete}
                    style={{
                      ...navButtonStyle,
                      backgroundColor: isUploadComplete ? '#232A5C' : navButtonStyle.backgroundColor,
                      color: isUploadComplete ? 'white' : navButtonStyle.color,
                      border: isUploadComplete ? '1px solid #232A5C' : navButtonStyle.border,
                    }}
                  >
                    Next
                  </Button>
                </Group>
              </Paper>
            )}
          </Stepper.Step>

          {/* Step 2: Custom Instructions */}
          <Stepper.Step label="Custom Instructions" description="Give Joy specific guidance">
            {activeStep === 1 && (
              <>
                <Paper withBorder shadow="sm" p="24px" style={{ borderRadius: '12px', border: '1px solid #e9ecef' }}>
                  <Group justify="space-between" align="center" mb={16}>
                    <Text
                      size="sm"
                      style={{ fontSize: '15px', lineHeight: '1.5', color: '#232A5C', margin: 0, fontWeight: 600 }}
                    >
                      Give Joy specific instructions so that the summary can be tailored to your audience and what
                      information they care most about.
                    </Text>
                    <Tooltip label="View saved instructions" withArrow>
                      <Button
                        data-test-id="DD-Summarizer-View-Saved-Instructions"
                        variant="subtle"
                        size="sm"
                        leftSection={<IconBookmark size={14} style={{ marginRight: 2 }} />}
                        onClick={() => setPromptPopoverOpen(true)}
                        style={{ fontWeight: 600 }}
                      >
                        Saved Instructions
                      </Button>
                    </Tooltip>
                  </Group>
                  <Text
                    size="xs"
                    c="dimmed"
                    style={{ fontSize: '13px', lineHeight: '1.5', color: '#6c757d', margin: '0 0 8px 0' }}
                  >
                    For example: "Focus the summary on identifying risk factors that may impact asset value, such as
                    high delinquency rates, significant repair costs, or poor unit conditions. Include a breakdown by
                    unit type if available. Highlight any discrepancies between the property's financial performance and
                    market standards."
                  </Text>
                  <Textarea
                    data-test-id="DD-Summarizer-Custom-Instructions"
                    placeholder="Enter your custom instructions here..."
                    value={promptText}
                    onChange={(e) => setPromptText(e.target.value)}
                    minRows={4}
                    maxRows={8}
                    style={{
                      fontFamily: 'monospace',
                      fontSize: '14px',
                      lineHeight: '1.5',
                      border: '1px solid #e9ecef',
                      borderRadius: '12px',
                    }}
                  />
                  <Group justify="space-between" mt="lg">
                    <Button
                      data-test-id="DD-Summarizer-Back-Button"
                      variant="default"
                      onClick={() => setActiveStep((s) => Math.max(s - 1, 0))}
                      style={navButtonStyle}
                    >
                      Back
                    </Button>
                    <Button
                      data-test-id="DD-Summarizer-Next-Button"
                      onClick={() => setActiveStep((s) => s + 1)}
                      disabled={!isInstructionsComplete}
                      style={{
                        ...navButtonStyle,
                        backgroundColor: isInstructionsComplete ? '#232A5C' : navButtonStyle.backgroundColor,
                        color: isInstructionsComplete ? 'white' : navButtonStyle.color,
                        border: isInstructionsComplete ? '1px solid #232A5C' : navButtonStyle.border,
                      }}
                    >
                      Next
                    </Button>
                  </Group>
                </Paper>
                <Modal
                  opened={promptPopoverOpen}
                  onClose={() => setPromptPopoverOpen(false)}
                  title="Manage custom instructions"
                  centered
                  size="md"
                  styles={{
                    content: {
                      borderRadius: '12px',
                      border: '1px solid #e9ecef',
                      padding: '24px',
                    },
                    header: {
                      borderBottom: 'none',
                      paddingBottom: '16px',
                      marginBottom: '8px',
                    },
                    title: {
                      fontSize: '18px',
                      fontWeight: 600,
                      color: '#232A5C',
                    },
                  }}
                >
                  <Stack gap="lg">
                    <Box>
                      <Text size="sm" c="dimmed" mb="md" fw={500} style={{ letterSpacing: '0.5px' }}>
                        Saved instructions
                      </Text>
                      {savedPrompts.length > 0 ? (
                        <ScrollArea.Autosize mah={200}>
                          <Stack gap="xs">
                            {filteredPrompts.map((prompt: any) => (
                              <Group
                                data-test-id="DD-Summarizer-Select-Instruction"
                                key={prompt.name}
                                justify="space-between"
                                p="sm"
                                className={`outline-item ${selectedPromptName === prompt.name ? 'selected' : ''}`}
                                onClick={() => handleSelectPrompt(prompt)}
                                style={{
                                  borderRadius: '6px',
                                  border:
                                    selectedPromptName === prompt.name ? '1px solid #635BFF' : '1px solid #f1f3f4',
                                  backgroundColor: selectedPromptName === prompt.name ? '#f8f9ff' : 'transparent',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                <Group gap="xs" align="center">
                                  <Text size="sm" fw={selectedPromptName === prompt.name ? 600 : 400}>
                                    {prompt.name}
                                  </Text>
                                  {selectedPromptName === prompt.name && (
                                    <Badge
                                      size="xs"
                                      color="blue"
                                      variant="filled"
                                      style={{ backgroundColor: '#635BFF' }}
                                    >
                                      Selected
                                    </Badge>
                                  )}
                                </Group>
                                <ActionIcon
                                  data-test-id="DD-Summarizer-Delete-Instruction"
                                  variant="transparent"
                                  c="gray.6"
                                  size="sm"
                                  radius="xl"
                                  className="close-icon-hover"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setPromptPopoverOpen(false)
                                    setTimeout(() => openDeleteModal('prompt', prompt.name), 100)
                                  }}
                                  title={`Delete "${prompt.name}"`}
                                >
                                  <IconTrash size={14} color="red" />
                                </ActionIcon>
                              </Group>
                            ))}
                          </Stack>
                        </ScrollArea.Autosize>
                      ) : (
                        <Text
                          size="sm"
                          c="dimmed"
                          style={{
                            fontStyle: 'italic',
                            padding: '16px',
                            textAlign: 'center',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '6px',
                          }}
                        >
                          No saved instructions
                        </Text>
                      )}
                    </Box>
                    <Box pt="md" style={{ borderTop: '1px solid #e9ecef' }}>
                      <Text size="sm" c="dimmed" mb="md" fw={500} style={{ letterSpacing: '0.5px' }}>
                        Save current instructions
                      </Text>
                      <Group gap="xs">
                        <TextInput
                          data-test-id="DD-Summarizer-Instruction-Name"
                          placeholder="Enter instruction name..."
                          value={newPromptName}
                          onChange={(e) => setNewPromptName(e.target.value)}
                          size="sm"
                          style={{ flex: 1 }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveNewPrompt()
                            }
                          }}
                        />
                        <Button
                          data-test-id="DD-Summarizer-Save-Instruction"
                          size="sm"
                          variant="filled"
                          style={{ backgroundColor: '#635BFF', borderRadius: '6px', color: 'white' }}
                          onClick={handleSaveNewPrompt}
                        >
                          Save
                        </Button>
                      </Group>
                    </Box>
                  </Stack>
                </Modal>
              </>
            )}
          </Stepper.Step>

          {/* Step 3: Report Outline */}
          <Stepper.Step label="Report Outline" description="Customize your report structure">
            {activeStep === 2 && (
              <>
                <Paper withBorder shadow="sm" p="24px" style={{ borderRadius: '12px', border: '1px solid #e9ecef' }}>
                  <Group justify="space-between" align="center" mb={16}>
                    <Text
                      size="sm"
                      style={{ fontSize: '15px', lineHeight: '1.5', color: '#232A5C', margin: 0, fontWeight: 600 }}
                    >
                      Customize the order and headings for your report.
                    </Text>
                    <Tooltip label="View saved outlines" withArrow>
                      <Button
                        data-test-id="DD-Summarizer-View-Saved-Outlines"
                        variant="subtle"
                        size="sm"
                        leftSection={<IconBookmark size={14} style={{ marginRight: 2 }} />}
                        onClick={() => setOutlinesModalOpen(true)}
                        style={{ fontWeight: 600 }}
                      >
                        Saved Outlines
                      </Button>
                    </Tooltip>
                  </Group>
                  <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={sections} strategy={verticalListSortingStrategy}>
                      <Box component="ul" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {sections.map((section) => (
                          <SortableItem key={section} id={section} onRemove={handleRemoveSection} />
                        ))}
                      </Box>
                    </SortableContext>
                  </DndContext>
                  <Group gap="xs">
                    <TextInput
                      data-test-id="DD-Summarizer-New-Heading"
                      placeholder="New heading"
                      value={newSection}
                      onChange={(e) => setNewSection(e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <Button
                      data-test-id="DD-Summarizer-Add-Heading"
                      onClick={handleAddSectionAndClose}
                      variant="filled"
                      style={{ backgroundColor: '#635BFF', borderRadius: '12px', color: 'white' }}
                    >
                      Add
                    </Button>
                  </Group>
                  <Group justify="space-between" mt="lg">
                    <Button
                      data-test-id="DD-Summarizer-Back-Button"
                      variant="default"
                      onClick={() => setActiveStep((s) => Math.max(s - 1, 0))}
                      style={navButtonStyle}
                    >
                      Back
                    </Button>
                    <Button
                      data-test-id="DD-Summarizer-Next-Button"
                      onClick={() => setActiveStep((s) => s + 1)}
                      disabled={!isOutlineComplete}
                      style={{
                        ...navButtonStyle,
                        backgroundColor: isOutlineComplete ? '#232A5C' : navButtonStyle.backgroundColor,
                        color: isOutlineComplete ? 'white' : navButtonStyle.color,
                        border: isOutlineComplete ? '1px solid #232A5C' : navButtonStyle.border,
                      }}
                    >
                      Next
                    </Button>
                  </Group>
                </Paper>
                <Modal
                  opened={outlinesModalOpen}
                  onClose={() => setOutlinesModalOpen(false)}
                  title="Manage report outlines"
                  centered
                  size="md"
                  styles={{
                    content: {
                      borderRadius: '12px',
                      border: '1px solid #e9ecef',
                      padding: '24px',
                    },
                    header: {
                      borderBottom: 'none',
                      paddingBottom: '16px',
                      marginBottom: '8px',
                    },
                    title: {
                      fontSize: '18px',
                      fontWeight: 600,
                      color: '#232A5C',
                    },
                  }}
                >
                  <Stack gap="lg">
                    <Box>
                      <Text size="sm" c="dimmed" mb="md" fw={500} style={{ letterSpacing: '0.5px' }}>
                        Saved outlines
                      </Text>
                      {savedTemplates.length > 0 ? (
                        <ScrollArea.Autosize mah={200}>
                          <Stack gap="xs">
                            {filteredOutlines.map((template: any) => (
                              <Group
                                key={template.name}
                                justify="space-between"
                                p="sm"
                                className={`outline-item ${selectedOutlineName === template.name ? 'selected' : ''}`}
                                onClick={() => handleSelectOutline(template)}
                                style={{
                                  borderRadius: '6px',
                                  border:
                                    selectedOutlineName === template.name ? '1px solid #635BFF' : '1px solid #f1f3f4',
                                  backgroundColor: selectedOutlineName === template.name ? '#f8f9ff' : 'transparent',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                <Group gap="xs" align="center">
                                  <Text size="sm" fw={selectedOutlineName === template.name ? 600 : 400}>
                                    {template.name}
                                  </Text>
                                  {selectedOutlineName === template.name && (
                                    <Badge
                                      size="xs"
                                      color="blue"
                                      variant="filled"
                                      style={{ backgroundColor: '#635BFF' }}
                                    >
                                      Selected
                                    </Badge>
                                  )}
                                </Group>
                                <ActionIcon
                                  data-test-id="DD-Summarizer-Delete-Outline"
                                  variant="transparent"
                                  c="gray.6"
                                  size="sm"
                                  radius="xl"
                                  className="close-icon-hover"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setOutlinesModalOpen(false)
                                    setTimeout(() => openDeleteModal('template', template.name), 100)
                                  }}
                                  title={`Delete "${template.name}"`}
                                >
                                  <IconTrash size={14} color="red" />
                                </ActionIcon>
                              </Group>
                            ))}
                          </Stack>
                        </ScrollArea.Autosize>
                      ) : (
                        <Text
                          size="sm"
                          c="dimmed"
                          style={{
                            fontStyle: 'italic',
                            padding: '16px',
                            textAlign: 'center',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '6px',
                          }}
                        >
                          No saved outlines
                        </Text>
                      )}
                    </Box>
                    <Box pt="md" style={{ borderTop: '1px solid #e9ecef' }}>
                      <Text size="sm" c="dimmed" mb="md" fw={500} style={{ letterSpacing: '0.5px' }}>
                        Save current outline
                      </Text>
                      <Group gap="xs">
                        <TextInput
                          data-test-id="DD-Summarizer-Outline-Name"
                          placeholder="Enter outline name..."
                          value={newOutlineName}
                          onChange={(e) => setNewOutlineName(e.target.value)}
                          size="sm"
                          style={{ flex: 1 }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveNewOutline()
                            }
                          }}
                        />
                        <Button
                          data-test-id="DD-Summarizer-Save-Outline"
                          size="sm"
                          variant="filled"
                          style={{ backgroundColor: '#635BFF', borderRadius: '6px', color: 'white' }}
                          onClick={handleSaveNewOutline}
                        >
                          Save
                        </Button>
                      </Group>
                    </Box>
                  </Stack>
                </Modal>
              </>
            )}
          </Stepper.Step>

          {/* Step 4: Joy Summary */}
          <Stepper.Step label="Joy Summary" description="Generate and edit your summary">
            {activeStep === 3 && (
              <Paper withBorder shadow="sm" p="24px" style={{ borderRadius: '12px', border: '1px solid #e9ecef' }}>
                <Group justify="space-between" align="center" mb={16}>
                  <Text
                    size="sm"
                    style={{ fontSize: '15px', lineHeight: '1.5', color: '#232A5C', margin: 0, fontWeight: 600 }}
                  >
                    Click Generate Summary when you are ready. Once Joy has finished summarizing, you can edit the summary below before exporting.
                  </Text>
                  <Group align="center" gap={8}>
                    {summaryText.trim() && (
                      <>
                        <Button
                          data-test-id="DD-Summarizer-Copy-Summary"
                          variant="light"
                          size="sm"
                          leftSection={<IconCopy size={16} />}
                          onClick={handleCopyToClipboard}
                          style={{
                            backgroundColor: '#f8fafc',
                            color: '#64748b',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            fontWeight: 600,
                          }}
                        >
                          Copy
                        </Button>
                        <Menu shadow="md" width={200}>
                          <Menu.Target>
                            <Button
                              data-test-id="DD-Summarizer-Export-Summary"
                              variant="light"
                              size="sm"
                              leftSection={<IconFileExport size={16} />}
                              style={{
                                backgroundColor: '#f8fafc',
                                color: '#64748b',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                fontWeight: 600,
                              }}
                            >
                              Export
                            </Button>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              data-test-id="DD-Summarizer-Export-PDF"
                              leftSection={<IconFileExport size={16} />}
                              onClick={handleExportPDF}
                            >
                              Export to PDF
                            </Menu.Item>
                            <Menu.Item
                              data-test-id="DD-Summarizer-Export-Word"
                              leftSection={<IconFileExport size={16} />}
                              onClick={handleExportWord}
                            >
                              Export to Word
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </>
                    )}
                    <Tooltip
                      label={disabledTooltipLabel}
                      disabled={allStepsReady || isGenerating}
                      withArrow
                      position="top"
                      multiline
                      w={220}
                      style={{ textAlign: 'center' }}
                    >
                      <Box>
                        <Button
                          data-test-id="DD-Summarizer-Generate-Summary"
                          variant="filled"
                          className={allStepsReady && !isGenerating ? 'generate-button-enabled' : ''}
                          style={{
                            borderRadius: '8px',
                            color: !allStepsReady ? '#adb5bd' : 'white',
                            backgroundColor: !allStepsReady ? '#e2e8f0' : '#635BFF',
                            fontWeight: 600,
                          }}
                          onClick={handleGenerateSummary}
                          disabled={!allStepsReady || isGenerating}
                          size="sm"
                          leftSection={isGenerating ? <Loader size="xs" color="white" /> : undefined}
                        >
                          {isGenerating ? 'Generating...' : 'Generate Summary'}
                        </Button>
                      </Box>
                    </Tooltip>
                  </Group>
                </Group>
                <TiptapEditor
                  value={summaryText}
                  onChange={setSummaryText}
                  placeholder={isGenerating ? 'Joy is summarizing...' : 'Joy is ready to summarize.'}
                  disabled={isGenerating}
                  markdown={summaryText.trim() ? summaryText : undefined}
                />
                <Group justify="space-between" mt="lg">
                  <Button
                    data-test-id="DD-Summarizer-Back-Button"
                    variant="default"
                    onClick={() => setActiveStep((s) => Math.max(s - 1, 0))}
                    style={navButtonStyle}
                  >
                    Back
                  </Button>
                </Group>
              </Paper>
            )}
          </Stepper.Step>
        </Stepper>
      </Box>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Confirmation"
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            Are you sure you want to delete this {deleteType === 'template' ? 'outline' : 'prompt'}?
          </Text>
          <Text size="sm" fw={600} style={{ color: '#232A5C' }}>
            "{itemToDelete}"
          </Text>
          <Text size="xs" c="dimmed">
            This action cannot be undone.
          </Text>

          <Group justify="flex-end" gap="sm" mt="md">
            <Button
              data-test-id="DD-Summarizer-Cancel-Delete"
              variant="subtle"
              color="gray"
              onClick={() => setDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              data-test-id="DD-Summarizer-Confirm-Delete"
              variant="filled"
              color="red"
              onClick={handleConfirmDelete}
              style={{
                backgroundColor: '#fa5252',
                '&:hover': {
                  backgroundColor: '#e03131',
                },
              }}
            >
              Yes, Delete this {deleteType === 'template' ? 'Outline' : 'Prompt'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  )
}
